/**
 * The masthead kicker, when the module strip cannot supply it.
 *
 * `modules.ts` gives every module one kicker, and for a module with one screen that is the
 * whole story. Two modules now have more than one. Module 06 carries the Reporting page at
 * `/marking` — kicker `Report Progress Across The Whole Panel · Open Any Official To Write
 * Theirs` (`05-reporting-marking.md` §A) — and the final marking screen at `/marking/:id`,
 * whose kicker §B3a states as `Final Marking · Whole Tournament`. Module 05 carries the
 * roster and the individual profile, whose kicker is not a constant at all:
 * `{Role} · {CTRY} · Profile, Appointments And Reporting` (`04-officials-profiles.md` §B).
 *
 * **Why a claim and not a table.** A second lookup table keyed by route pattern would serve
 * module 06 and could not serve module 05, because a profile's kicker is composed from the
 * official the page has just fetched. So the page states its own line, and the masthead
 * prefers it over the module's default.
 *
 * **Why the claim carries its path.** The claim lives above the router outlet, so it
 * outlives the page that made it by one render on the way to somewhere else. Pinning it to
 * the pathname it was made for means a stale kicker can never appear over another screen:
 * the masthead asks for THIS path and gets the module default unless the page mounted at it
 * said otherwise.
 *
 * **No provider is a no-op**, so a page rendered on its own in a component test does not
 * have to be wrapped in one to render at all.
 */

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

/** One page's claim: the line, and the path it was made for. */
interface KickerClaim {
  path: string;
  kicker: string;
}

interface PageKickerValue {
  claim: KickerClaim | null;
  setClaim: (claim: KickerClaim | null) => void;
}

const PageKickerContext = createContext<PageKickerValue | null>(null);

export function PageKickerProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [claim, setClaim] = useState<KickerClaim | null>(null);
  const value = useMemo<PageKickerValue>(() => ({ claim, setClaim }), [claim]);

  return <PageKickerContext.Provider value={value}>{children}</PageKickerContext.Provider>;
}

/**
 * A page states the kicker its own spec gives it. Pass `null` while the page does not yet
 * know its line — a profile before its official has loaded — and the module's default stands
 * until it does, rather than a half-composed one flashing over it.
 */
export function usePageKicker(kicker: string | null): void {
  const context = useContext(PageKickerContext);
  const { pathname } = useLocation();
  const setClaim = context?.setClaim;

  useEffect(() => {
    if (setClaim === undefined || kicker === null) return;
    setClaim({ path: pathname, kicker });
    return () => {
      setClaim(null);
    };
  }, [setClaim, pathname, kicker]);
}

/** The masthead's line: the claim made for this exact path, else the module's own. */
export function usePageKickerValue(pathname: string, fallback: string): string {
  const context = useContext(PageKickerContext);
  const claim = context?.claim ?? null;
  return claim !== null && claim.path === pathname ? claim.kicker : fallback;
}
